import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, Phone, Star, MapPin, ArrowUpRight, Check } from "lucide-react";
import { IMG, waLink, PHONE_DISPLAY } from "@/lib/site";
import { EASE } from "@/lib/motion";

const LINES = ["Box para Banheiro,", "Espelhos & Vidros", "Temperados."];
const CHECKS = ["Box para Banheiro", "Espelhos Sob Medida", "Vidro Temperado", "Manutenção Especializada"];

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: EASE } }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yBadge = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen overflow-hidden bg-white pt-28 pb-16 lg:pt-32">
      <div className="absolute inset-0 mesh-bg" />
      <div className="grain" />

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-8 items-center">
        {/* Left */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="inline-flex items-center gap-2 rounded-full border border-[#012a60]/15 bg-white/70 backdrop-blur px-4 py-1.5 mb-7"
          >
            <MapPin size={14} className="text-[#55c0e0]" />
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#012a60]">
              São José • Florianópolis • Grande Florianópolis
            </span>
          </motion.div>

          <h1 className="font-display font-black tracking-tighter leading-[0.95] text-4xl sm:text-6xl lg:text-7xl text-[#012a60]">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="block"
                  variants={lineVariant}
                  custom={i}
                  initial="hidden"
                  animate="show"
                >
                  {i === 1 ? (
                    <>Espelhos & <span className="text-[#55c0e0]">Vidros</span></>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
            className="mt-6 max-w-xl text-base lg:text-lg text-slate-600 leading-relaxed"
          >
            Mais do que instalar vidros, entregamos <strong className="text-[#012a60]">qualidade, segurança
            e acabamento impecável</strong> para sua residência ou empresa.
          </motion.p>

          <motion.ul
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75, ease: EASE }}
            className="mt-7 grid grid-cols-2 gap-x-6 gap-y-3 max-w-lg"
          >
            {CHECKS.map((c) => (
              <li key={c} className="flex items-center gap-2 text-sm font-medium text-[#012a60]">
                <span className="grid place-items-center h-5 w-5 rounded-full bg-[#55c0e0]/20">
                  <Check size={13} className="text-[#012a60]" />
                </span>
                {c}
              </li>
            ))}
          </motion.ul>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-orcamento-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-[#012a60] px-7 py-4 font-bold text-white hover:bg-[#55c0e0] hover:text-[#012a60] transition-colors"
            >
              Solicitar Orçamento
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="inline-flex items-center gap-2 rounded-full border border-[#012a60]/20 bg-white px-7 py-4 font-bold text-[#012a60] hover:border-[#55c0e0] transition-colors"
            >
              <MessageCircle size={18} className="text-[#25D366]" /> Falar no WhatsApp
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 1.05 }}
            className="mt-8 flex flex-wrap items-center gap-6"
          >
            <div className="flex items-center gap-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} className="fill-[#f5b301] text-[#f5b301]" />
                ))}
              </div>
              <span className="text-sm font-semibold text-[#012a60]">5 estrelas no Google</span>
            </div>
            <a href={`tel:+${"5548996889862"}`} className="flex items-center gap-2 text-sm font-semibold text-[#012a60]">
              <Phone size={15} className="text-[#55c0e0]" /> {PHONE_DISPLAY}
            </a>
          </motion.div>
        </div>

        {/* Right image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.3, ease: EASE }}
          className="relative"
        >
          <div className="relative aspect-[4/5] w-full max-w-md mx-auto lg:mx-0 lg:ml-auto rounded-[2rem] overflow-hidden ring-1 ring-black/5 shadow-[0_30px_80px_-20px_rgba(1,42,96,0.45)]">
            <motion.img
              style={{ y: yImg }}
              src={IMG.boxPreto}
              alt="Box de vidro temperado premium instalado pela Gabibox"
              className="absolute inset-0 h-[115%] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#012a60]/40 via-transparent to-transparent" />
          </div>

          {/* floating badge */}
          <motion.div
            style={{ y: yBadge }}
            className="absolute -left-2 sm:-left-6 bottom-10 w-52 rounded-2xl bg-white/80 backdrop-blur-xl border border-white/60 p-4 shadow-xl"
          >
            <div className="flex items-center gap-1 mb-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={13} className="fill-[#f5b301] text-[#f5b301]" />
              ))}
            </div>
            <p className="text-xs text-slate-600 leading-snug">
              <strong className="text-[#012a60]">Referência</strong> em vidros temperados na Grande Florianópolis.
            </p>
          </motion.div>

          <motion.div
            style={{ y: yImg }}
            className="absolute -right-3 top-8 rounded-2xl bg-[#012a60] px-5 py-4 text-white shadow-xl"
          >
            <p className="font-display text-3xl font-black leading-none">+10</p>
            <p className="text-[11px] tracking-wide text-white/70">anos de experiência</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
